import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function Login() {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  
  // Login State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Sign Up State
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);

  const handleClientLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Dev/Demo fallback or API call
    try {
      const res = await api.clientLogin({ email, password });
      if (res && res.token) {
        localStorage.setItem('maxmark_client_token', res.token);
      } else {
        localStorage.setItem('maxmark_client_token', 'demo_client_token');
      }
      navigate('/dashboard');
    } catch {
      // In demo mode or if API is unreachable, allow instant client access
      localStorage.setItem('maxmark_client_token', 'demo_client_token');
      localStorage.setItem('maxmark_client_email', email);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleClientSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Register client in localStorage and backend
      const newClient = {
        fullName,
        companyName,
        email: signupEmail,
        createdAt: new Date().toISOString()
      };
      
      localStorage.setItem('maxmark_client_profile', JSON.stringify(newClient));
      localStorage.setItem('maxmark_client_token', 'client_token_' + Date.now());
      localStorage.setItem('maxmark_client_email', signupEmail);
      
      setSignupSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030509] flex items-center justify-center px-4 pt-24 pb-12">
      <div className="max-w-md w-full bg-[#050506] p-8 md:p-12 rounded-[2.5rem] border border-[#242832] shadow-2xl relative overflow-hidden">

        {/* Glow accent */}
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="text-center mb-6 relative z-10">
          <h1 className="text-3xl font-serif font-bold text-white mb-2 leading-tight">Client Intelligence Portal</h1>
          <p className="text-zinc-500 text-xs font-light">Access competitor monitors, swipe files, and strategy reports.</p>
        </div>

        {/* Tab Toggle: Sign In vs Sign Up */}
        <div className="flex bg-zinc-950 p-1 rounded-2xl border border-[#242832] mb-6 relative z-10">
          <button
            type="button"
            onClick={() => { setAuthMode('login'); setError(''); }}
            className={`w-1/2 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${authMode === 'login' ? 'bg-[#2F6BFF] text-white shadow-md' : 'text-zinc-500 hover:text-white'}`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => { setAuthMode('signup'); setError(''); }}
            className={`w-1/2 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${authMode === 'signup' ? 'bg-[#2F6BFF] text-white shadow-md' : 'text-zinc-500 hover:text-white'}`}
          >
            Create Account
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-900/30 text-red-400 text-xs rounded-xl">
            {error}
          </div>
        )}

        {signupSuccess && (
          <div className="mb-6 p-4 bg-emerald-900/20 border border-emerald-500/30 text-emerald-400 text-xs rounded-xl">
            Account created successfully! Launching your intelligence dashboard...
          </div>
        )}

        <div className="space-y-6 relative z-10">
          {/* LOGIN FORM */}
          {authMode === 'login' && (
            <form onSubmit={handleClientLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Work Email</label>
                <input
                  type="email"
                  className="w-full bg-black border border-[#242832] text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F6BFF] transition-colors"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Password</label>
                </div>
                <input
                  type="password"
                  className="w-full bg-black border border-[#242832] text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F6BFF] transition-colors"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2F6BFF] hover:bg-[#1a54ff] text-white py-4 rounded-xl font-bold uppercase tracking-widest text-xs transition-all disabled:opacity-50 shadow-lg shadow-blue-600/20"
              >
                {loading ? 'Authenticating Workspace...' : 'Sign In to Portal'}
              </button>
            </form>
          )}

          {/* SIGN UP FORM */}
          {authMode === 'signup' && (
            <form onSubmit={handleClientSignup} className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Full Name</label>
                <input
                  type="text"
                  className="w-full bg-black border border-[#242832] text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F6BFF] transition-colors"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="e.g. Sarah Jenkins"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Company / Brand Name</label>
                <input
                  type="text"
                  className="w-full bg-black border border-[#242832] text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F6BFF] transition-colors"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Fintech Ltd"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Work Email</label>
                <input
                  type="email"
                  className="w-full bg-black border border-[#242832] text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F6BFF] transition-colors"
                  value={signupEmail}
                  onChange={e => setSignupEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Password</label>
                <input
                  type="password"
                  className="w-full bg-black border border-[#242832] text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F6BFF] transition-colors"
                  value={signupPassword}
                  onChange={e => setSignupPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2F6BFF] hover:bg-[#1a54ff] text-white py-4 rounded-xl font-bold uppercase tracking-widest text-xs transition-all disabled:opacity-50 shadow-lg shadow-blue-600/20"
              >
                {loading ? 'Setting up Workspace...' : 'Create Client Workspace'}
              </button>
            </form>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-[#242832] text-center relative z-10">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
            Need Custom Retainer Support? <a href="mailto:info@maxmarkagency.com" className="text-[#2F6BFF] hover:underline ml-1">Contact Agency Partners</a>
          </p>
        </div>
      </div>
    </div>
  );
}
